const STATUS_ORDER = {
  running: 0,
  completed: 1,
  failed: 2,
  blocked: 3,
  stopped: 4,
};

const TERMINAL_STATUSES = new Set(["completed", "failed", "blocked", "stopped"]);

const ACTIVITY_STEP_MATCHERS = [
  {
    step_id: "understanding_request",
    title: "Understanding request",
    pattern: /(understanding|reading|interpreting).*(request|question|prompt)/i,
  },
  {
    step_id: "detecting_topic_intent",
    title: "Detecting topic/intent",
    pattern: /(detecting|identifying).*(topic|intent|goal)/i,
  },
  {
    step_id: "routing_request",
    title: "Routing request",
    pattern: /(routing|selecting|choosing).*(workflow|agent|tool|route)/i,
  },
  {
    step_id: "searching_uploaded_documents",
    title: "Searching uploaded documents",
    pattern: /(searching|reviewing|checking).*(uploaded|document|knowledge base|kb|repository|files)/i,
  },
  {
    step_id: "searching_external_sources",
    title: "Searching external sources",
    pattern: /(searching|reviewing|checking).*(external|web|internet|online|source)/i,
  },
  {
    step_id: "collecting_sources",
    title: "Collecting sources",
    pattern: /(collecting|gathering|retrieving).*(source|citation|reference)/i,
  },
  {
    step_id: "analyzing_findings",
    title: "Analyzing findings",
    pattern: /(analyzing|evaluating|synthesizing).*(finding|result|evidence|information)/i,
  },
  {
    step_id: "preparing_answer",
    title: "Preparing answer",
    pattern: /(preparing|structuring|organizing).*(answer|response|reply)/i,
  },
  {
    step_id: "drafting_response",
    title: "Drafting response",
    pattern: /(drafting|writing|generating).*(response|answer|reply)/i,
  },
  {
    step_id: "finalizing_response",
    title: "Finalizing response",
    pattern: /(finalizing|finishing|wrapping up|completing).*(response|answer|reply)?/i,
  },
];

const normalizeString = (value) => String(value ?? "").trim();

const toSentenceCase = (value) => {
  const text = normalizeString(value);
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const slugify = (value) =>
  normalizeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "step";

const cloneArtifact = (artifact = {}) => ({
  run_id: artifact?.run_id || null,
  event_seq:
    Number.isFinite(Number(artifact?.event_seq)) && Number(artifact.event_seq) >= 0
      ? Number(artifact.event_seq)
      : null,
  timestamp: artifact?.timestamp || null,
  category: normalizeString(artifact?.category) || "artifact",
  type: normalizeString(artifact?.type) || "artifact.created",
  parent_step_id: normalizeString(artifact?.parent_step_id) || null,
  title: normalizeString(artifact?.title),
  status: normalizeString(artifact?.status) || "completed",
  metadata:
    artifact?.metadata && typeof artifact.metadata === "object"
      ? { ...artifact.metadata }
      : {},
});

const cloneTrace = (trace = {}) => ({
  run_id: trace?.run_id || null,
  status: trace?.status || "running",
  steps: Array.isArray(trace?.steps)
    ? trace.steps.map((step) => ({
        ...step,
        metadata:
          step?.metadata && typeof step.metadata === "object"
            ? {
                ...step.metadata,
                artifacts: Array.isArray(step?.metadata?.artifacts)
                  ? step.metadata.artifacts.map((artifact) => cloneArtifact(artifact))
                  : [],
              }
            : { artifacts: [] },
      }))
    : [],
  artifacts: Array.isArray(trace?.artifacts)
    ? trace.artifacts.map((artifact) => cloneArtifact(artifact))
    : [],
  latestMessage: trace?.latestMessage || "",
  summary: trace?.summary || "",
  sourcesCount: Number(trace?.sourcesCount || 0) || 0,
  durationMs:
    Number.isFinite(Number(trace?.durationMs)) && Number(trace.durationMs) >= 0
      ? Number(trace.durationMs)
      : null,
  lastEventSeq:
    Number.isFinite(Number(trace?.lastEventSeq)) && Number(trace.lastEventSeq) >= 0
      ? Number(trace.lastEventSeq)
      : null,
  seenEventSeqs: Array.isArray(trace?.seenEventSeqs) ? [...trace.seenEventSeqs] : [],
  seenKeys: Array.isArray(trace?.seenKeys) ? [...trace.seenKeys] : [],
  hasStructuredExecution: Boolean(trace?.hasStructuredExecution),
});

export const createEmptyExecutionTrace = () =>
  cloneTrace({
    run_id: null,
    status: "running",
    steps: [],
    artifacts: [],
    latestMessage: "",
    summary: "",
    sourcesCount: 0,
    durationMs: null,
    lastEventSeq: null,
    seenEventSeqs: [],
    seenKeys: [],
    hasStructuredExecution: false,
  });

const isStructuredStep = (step = {}) =>
  step?.source === "step" ||
  step?.source === "execution" ||
  normalizeString(step?.category) === "step" ||
  normalizeString(step?.type).startsWith("step.") ||
  Number.isFinite(Number(step?.event_seq)) ||
  Boolean(normalizeString(step?.parent_step_id));

const countArtifacts = (trace = {}) => {
  const topLevel = Array.isArray(trace?.artifacts) ? trace.artifacts.length : 0;
  const stepLevel = Array.isArray(trace?.steps)
    ? trace.steps.reduce(
        (total, step) =>
          total + (Array.isArray(step?.metadata?.artifacts) ? step.metadata.artifacts.length : 0),
        0,
      )
    : 0;
  return topLevel + stepLevel;
};

const getTraceMetrics = (trace) => {
  const normalized = cloneTrace(trace);
  const steps = Array.isArray(normalized.steps) ? normalized.steps : [];
  return {
    structuredStepCount: steps.filter((step) => isStructuredStep(step)).length,
    totalSteps: steps.length,
    lastEventSeq:
      Number.isFinite(Number(normalized.lastEventSeq)) && Number(normalized.lastEventSeq) >= 0
        ? Number(normalized.lastEventSeq)
        : 0,
    nestedSteps: steps.filter((step) => normalizeString(step?.parent_step_id)).length,
    artifactCount: countArtifacts(normalized),
  };
};

const getStructuredDedupeKey = ({ run_id, step_id, type, status, title }) =>
  [
    normalizeString(run_id),
    slugify(step_id || title),
    normalizeString(type).toLowerCase(),
    normalizeString(status).toLowerCase(),
    normalizeString(title).toLowerCase(),
  ].join("|");

const inferActivityDescriptor = (payload = {}) => {
  const message = normalizeString(payload?.message || payload?.summary || payload?.title);
  if (!message) return null;

  const cleaned = message.replace(/[.]+$/g, "").replace(/\s+/g, " ").trim();
  const matched = ACTIVITY_STEP_MATCHERS.find(({ pattern }) => pattern.test(cleaned));
  const title = matched?.title || toSentenceCase(cleaned);
  const step_id = matched?.step_id || slugify(title);

  return {
    type: "step.running",
    step_id,
    title,
    status:
      normalizeString(payload?.status).toLowerCase() === "completed"
        ? "completed"
        : "running",
    summary: cleaned,
    metadata:
      payload?.metadata && typeof payload.metadata === "object"
        ? payload.metadata
        : {},
    timestamp: payload?.timestamp || null,
    category: "step",
  };
};

const updateSourcesCount = (trace, eventData = {}) => {
  const counts = [
    trace.sourcesCount,
    eventData?.metadata?.sources,
    eventData?.metadata?.sources_count,
    eventData?.sources_count,
    Array.isArray(eventData?.citations) ? eventData.citations.length : 0,
    Array.isArray(eventData?.rag_sources) ? eventData.rag_sources.length : 0,
  ]
    .map((value) => Number(value || 0) || 0)
    .filter((value) => value > 0);

  return counts.length > 0 ? Math.max(...counts) : trace.sourcesCount || 0;
};

const computeTraceSummary = (trace) => {
  const nextTrace = cloneTrace(trace);
  const steps = Array.isArray(nextTrace.steps) ? nextTrace.steps : [];
  const completedCount = steps.filter((step) => step.status === "completed").length;
  const failedCount = steps.filter((step) => step.status === "failed").length;
  const blockedCount = steps.filter((step) => step.status === "blocked").length;
  const totalSteps = steps.length;
  const status =
    blockedCount > 0
      ? "blocked"
      : failedCount > 0
        ? "failed"
        : nextTrace.status === "stopped"
          ? "stopped"
          : nextTrace.status === "completed" || (totalSteps > 0 && completedCount === totalSteps)
            ? "completed"
            : "running";

  const summaryParts = [];
  summaryParts.push(
    status === "completed"
      ? "Process completed"
      : status === "failed"
        ? "Process failed"
        : status === "blocked"
          ? "Process blocked"
          : status === "stopped"
            ? "Process stopped"
            : "Working on your request",
  );
  if (totalSteps > 0) {
    summaryParts.push(`${totalSteps} step${totalSteps === 1 ? "" : "s"}`);
  }
  if (nextTrace.sourcesCount > 0) {
    summaryParts.push(
      `${nextTrace.sourcesCount} source${nextTrace.sourcesCount === 1 ? "" : "s"}`,
    );
  }
  if (countArtifacts(nextTrace) > 0) {
    const totalArtifacts = countArtifacts(nextTrace);
    summaryParts.push(`${totalArtifacts} artifact${totalArtifacts === 1 ? "" : "s"}`);
  }

  return {
    ...nextTrace,
    status,
    summary: summaryParts.join(" · "),
  };
};

const closePreviousRunningActivityStep = (trace, nextStepId) => {
  const prior = [...trace.steps]
    .reverse()
    .find(
      (step) =>
        step.status === "running" &&
        step.source === "activity" &&
        step.step_id !== nextStepId,
    );

  if (!prior) return trace;

  return {
    ...trace,
    steps: trace.steps.map((step) =>
      step.step_id === prior.step_id && step.status === "running"
        ? {
            ...step,
            status: "completed",
          }
        : step,
    ),
  };
};

const pruneActivityFallbackSteps = (trace) => ({
  ...trace,
  steps: trace.steps.filter((step) => step.source !== "activity"),
});

const ensureStepArtifacts = (step = {}) => {
  const metadata =
    step?.metadata && typeof step.metadata === "object" ? { ...step.metadata } : {};
  if (!Array.isArray(metadata.artifacts)) metadata.artifacts = [];
  return {
    ...step,
    metadata,
  };
};

const upsertStep = (trace, normalizedEvent, source) => {
  let nextTrace = cloneTrace(trace);
  const stepId = normalizeString(normalizedEvent.step_id || normalizedEvent.title);
  const title = normalizeString(normalizedEvent.title || normalizedEvent.summary || stepId);
  if (!stepId || !title) return computeTraceSummary(nextTrace);

  const category = normalizeString(normalizedEvent.category) || "step";
  const type = normalizeString(normalizedEvent.type) || "step.running";
  const status =
    normalizeString(normalizedEvent.status || type.split(".").pop()).toLowerCase() || "running";
  const eventSeq = Number.isFinite(Number(normalizedEvent.event_seq))
    ? Number(normalizedEvent.event_seq)
    : null;
  const dedupeKey = getStructuredDedupeKey({
    run_id: normalizedEvent.run_id,
    step_id: stepId,
    type,
    status,
    title,
  });

  if (eventSeq != null) {
    if (nextTrace.seenEventSeqs.includes(eventSeq)) return computeTraceSummary(nextTrace);
    nextTrace.seenEventSeqs.push(eventSeq);
    nextTrace.lastEventSeq = Math.max(Number(nextTrace.lastEventSeq || 0) || 0, eventSeq);
  } else if (nextTrace.seenKeys.includes(dedupeKey)) {
    return computeTraceSummary(nextTrace);
  } else {
    nextTrace.seenKeys.push(dedupeKey);
  }

  if (source !== "activity" && !nextTrace.hasStructuredExecution) {
    nextTrace = pruneActivityFallbackSteps(nextTrace);
    nextTrace.hasStructuredExecution = true;
  }
  if (source === "activity" && nextTrace.hasStructuredExecution) {
    return computeTraceSummary(nextTrace);
  }

  const existingIndex = nextTrace.steps.findIndex((step) => step.step_id === stepId);
  const metadata =
    normalizedEvent?.metadata && typeof normalizedEvent.metadata === "object"
      ? { ...normalizedEvent.metadata }
      : {};

  if (source === "activity") {
    const updatedTrace = closePreviousRunningActivityStep(nextTrace, stepId);
    nextTrace.steps = updatedTrace.steps;
  }

  const nextStep = ensureStepArtifacts({
    step_id: stepId,
    parent_step_id: normalizeString(normalizedEvent.parent_step_id) || null,
    title,
    summary: normalizeString(normalizedEvent.summary),
    status,
    timestamp: normalizedEvent.timestamp || null,
    metadata,
    event_seq: eventSeq,
    type,
    category,
    source,
    run_id: normalizeString(normalizedEvent.run_id) || null,
    order:
      existingIndex >= 0
        ? nextTrace.steps[existingIndex]?.order ?? existingIndex
        : nextTrace.steps.length,
  });

  if (existingIndex >= 0) {
    const existingStep = ensureStepArtifacts(nextTrace.steps[existingIndex]);
    nextTrace.steps[existingIndex] = ensureStepArtifacts({
      ...existingStep,
      ...nextStep,
      summary: nextStep.summary || existingStep.summary || "",
      metadata: {
        ...(existingStep.metadata || {}),
        ...metadata,
        artifacts: Array.isArray(existingStep?.metadata?.artifacts)
          ? existingStep.metadata.artifacts.map((artifact) => cloneArtifact(artifact))
          : [],
      },
    });
  } else {
    nextTrace.steps.push(nextStep);
  }

  nextTrace.run_id = normalizedEvent.run_id || nextTrace.run_id || null;
  nextTrace.latestMessage = nextStep.summary || nextTrace.latestMessage;
  nextTrace.sourcesCount = updateSourcesCount(nextTrace, normalizedEvent);
  if (
    Number.isFinite(Number(metadata?.duration_ms)) &&
    Number(metadata.duration_ms) >= 0
  ) {
    nextTrace.durationMs = Math.max(
      Number(nextTrace.durationMs || 0) || 0,
      Number(metadata.duration_ms),
    );
  }

  return computeTraceSummary(nextTrace);
};

const attachArtifactToTrace = (trace, payload = {}) => {
  let nextTrace = cloneTrace(trace);
  const type = normalizeString(payload?.type) || "artifact.created";
  const eventSeq = Number.isFinite(Number(payload?.event_seq))
    ? Number(payload.event_seq)
    : null;
  const title =
    normalizeString(payload?.title) ||
    normalizeString(payload?.metadata?.filename) ||
    "Artifact";
  const dedupeKey = getStructuredDedupeKey({
    run_id: payload?.run_id,
    step_id: payload?.parent_step_id || title,
    type,
    status: payload?.status || "completed",
    title,
  });

  if (eventSeq != null) {
    if (nextTrace.seenEventSeqs.includes(eventSeq)) return computeTraceSummary(nextTrace);
    nextTrace.seenEventSeqs.push(eventSeq);
    nextTrace.lastEventSeq = Math.max(Number(nextTrace.lastEventSeq || 0) || 0, eventSeq);
  } else if (nextTrace.seenKeys.includes(dedupeKey)) {
    return computeTraceSummary(nextTrace);
  } else {
    nextTrace.seenKeys.push(dedupeKey);
  }

  if (!nextTrace.hasStructuredExecution) {
    nextTrace = pruneActivityFallbackSteps(nextTrace);
    nextTrace.hasStructuredExecution = true;
  }

  const artifact = cloneArtifact({
    ...payload,
    title,
    status: payload?.status || "completed",
  });

  if (
    !nextTrace.artifacts.some(
      (item) =>
        (item.event_seq != null && artifact.event_seq != null && item.event_seq === artifact.event_seq) ||
        (item.title === artifact.title &&
          normalizeString(item?.metadata?.filename) ===
            normalizeString(artifact?.metadata?.filename)),
    )
  ) {
    nextTrace.artifacts.push(artifact);
  }

  const parentStepId = normalizeString(payload?.parent_step_id);
  if (parentStepId) {
    nextTrace.steps = nextTrace.steps.map((step) => {
      if (step.step_id !== parentStepId) return step;
      const safeStep = ensureStepArtifacts(step);
      const currentArtifacts = Array.isArray(safeStep?.metadata?.artifacts)
        ? safeStep.metadata.artifacts.map((item) => cloneArtifact(item))
        : [];
      if (
        !currentArtifacts.some(
          (item) =>
            (item.event_seq != null && artifact.event_seq != null && item.event_seq === artifact.event_seq) ||
            (item.title === artifact.title &&
              normalizeString(item?.metadata?.filename) ===
                normalizeString(artifact?.metadata?.filename)),
        )
      ) {
        currentArtifacts.push(artifact);
      }
      return {
        ...safeStep,
        metadata: {
          ...safeStep.metadata,
          artifacts: currentArtifacts,
        },
      };
    });
  }

  nextTrace.run_id = payload?.run_id || nextTrace.run_id || null;
  nextTrace.latestMessage = title || nextTrace.latestMessage;
  if (
    Number.isFinite(Number(payload?.metadata?.duration_ms)) &&
    Number(payload.metadata.duration_ms) >= 0
  ) {
    nextTrace.durationMs = Math.max(
      Number(nextTrace.durationMs || 0) || 0,
      Number(payload.metadata.duration_ms),
    );
  }

  return computeTraceSummary(nextTrace);
};

const normalizeExecutionWrapper = (eventName, payload = {}) => {
  const data = payload && typeof payload === "object" ? payload : {};
  const event = normalizeString(eventName);

  if (event === "execution") {
    const innerType = normalizeString(data?.type);
    const category = normalizeString(data?.category);
    if (category === "artifact" || innerType.startsWith("artifact.")) {
      return { kind: "artifact", data };
    }
    if (category === "step" || innerType.startsWith("step.")) {
      return {
        kind: "step",
        data: {
          ...data,
          type: innerType || "step.running",
          status: data?.status || innerType.split(".").pop() || "running",
          step_id: data?.step_id || slugify(data?.title || innerType || "step"),
          title:
            data?.title ||
            toSentenceCase((data?.step_id || innerType || "step").replace(/_/g, " ")),
          summary: data?.summary || "",
        },
      };
    }
  }

  if (event.startsWith("step.")) {
    return {
      kind: "step",
      data: {
        ...data,
        type: data?.type || event,
        status: data?.status || event.split(".").pop(),
        step_id: data?.step_id || slugify(data?.title || event),
        title:
          data?.title || toSentenceCase((data?.step_id || event).replace(/_/g, " ")),
        summary: data?.summary || "",
      },
    };
  }

  return null;
};

export const choosePreferredExecutionTrace = (leftTrace, rightTrace) => {
  const left = computeTraceSummary(
    cloneTrace(
      leftTrace && typeof leftTrace === "object" ? leftTrace : createEmptyExecutionTrace(),
    ),
  );
  const right = computeTraceSummary(
    cloneTrace(
      rightTrace && typeof rightTrace === "object" ? rightTrace : createEmptyExecutionTrace(),
    ),
  );
  const leftMetrics = getTraceMetrics(left);
  const rightMetrics = getTraceMetrics(right);

  const compareOrder = [
    rightMetrics.structuredStepCount - leftMetrics.structuredStepCount,
    rightMetrics.lastEventSeq - leftMetrics.lastEventSeq,
    rightMetrics.artifactCount - leftMetrics.artifactCount,
    rightMetrics.nestedSteps - leftMetrics.nestedSteps,
    rightMetrics.totalSteps - leftMetrics.totalSteps,
  ];

  for (const delta of compareOrder) {
    if (delta > 0) return right;
    if (delta < 0) return left;
  }

  return left;
};

export const applyExecutionEvent = (trace, eventName, payload = {}) => {
  const baseTrace = cloneTrace(
    trace && typeof trace === "object" ? trace : createEmptyExecutionTrace(),
  );
  const event = normalizeString(eventName);
  const data = payload && typeof payload === "object" ? payload : {};

  if (!event) return computeTraceSummary(baseTrace);

  if (event === "activity") {
    if (baseTrace.hasStructuredExecution) return computeTraceSummary(baseTrace);
    const normalizedActivity = inferActivityDescriptor(data);
    if (!normalizedActivity) return computeTraceSummary(baseTrace);
    return upsertStep(baseTrace, normalizedActivity, "activity");
  }

  const executionEvent = normalizeExecutionWrapper(event, data);
  if (executionEvent?.kind === "step") {
    return upsertStep(baseTrace, executionEvent.data, "step");
  }
  if (executionEvent?.kind === "artifact") {
    return attachArtifactToTrace(baseTrace, executionEvent.data);
  }

  if (event === "done") {
    const localFinal = finalizeExecutionTrace(baseTrace, {
      terminalStatus: "completed",
      finalData: data,
    });
    const incomingTrace =
      data?.executionTrace && typeof data.executionTrace === "object"
        ? data.executionTrace
        : data?.result?.executionTrace && typeof data.result.executionTrace === "object"
          ? data.result.executionTrace
          : null;
    return incomingTrace ? choosePreferredExecutionTrace(localFinal, incomingTrace) : localFinal;
  }

  if (event === "error") {
    const localFinal = finalizeExecutionTrace(baseTrace, {
      terminalStatus: "failed",
      finalData: data,
    });
    return data?.executionTrace
      ? choosePreferredExecutionTrace(localFinal, data.executionTrace)
      : localFinal;
  }

  if (event === "blocked") {
    const localFinal = finalizeExecutionTrace(baseTrace, {
      terminalStatus: "blocked",
      finalData: data,
    });
    return data?.executionTrace
      ? choosePreferredExecutionTrace(localFinal, data.executionTrace)
      : localFinal;
  }

  if (event === "stop") {
    const localFinal = finalizeExecutionTrace(baseTrace, {
      terminalStatus: "stopped",
      finalData: data,
      addFinalizingStep: false,
    });
    return data?.executionTrace
      ? choosePreferredExecutionTrace(localFinal, data.executionTrace)
      : localFinal;
  }

  return computeTraceSummary(baseTrace);
};

export const finalizeExecutionTrace = (
  trace,
  { terminalStatus = "completed", finalData = {}, addFinalizingStep = true } = {},
) => {
  const nextTrace = cloneTrace(
    trace && typeof trace === "object" ? trace : createEmptyExecutionTrace(),
  );
  const desiredStatus =
    terminalStatus === "blocked"
      ? "blocked"
      : terminalStatus === "failed"
        ? "failed"
        : terminalStatus === "stopped"
          ? "stopped"
          : "completed";

  if (
    addFinalizingStep &&
    nextTrace.steps.length > 0 &&
    !nextTrace.steps.some((step) => step.step_id === "finalization")
  ) {
    nextTrace.steps.push(
      ensureStepArtifacts({
        step_id: "finalization",
        parent_step_id: null,
        title: "Finalizing response",
        summary: "",
        status: desiredStatus,
        timestamp: finalData?.timestamp || null,
        metadata: { artifacts: [] },
        event_seq: null,
        type: `step.${desiredStatus}`,
        category: "step",
        source: "derived",
        order: nextTrace.steps.length,
      }),
    );
  }

  nextTrace.steps = nextTrace.steps.map((step) =>
    step.status !== "running"
      ? ensureStepArtifacts(step)
      : { ...ensureStepArtifacts(step), status: desiredStatus },
  );

  nextTrace.status = desiredStatus;
  nextTrace.sourcesCount = updateSourcesCount(nextTrace, finalData);
  if (Number.isFinite(Number(finalData?.response_time))) {
    nextTrace.durationMs = Math.round(Number(finalData.response_time) * 1000);
  } else if (
    Number.isFinite(Number(finalData?.result?.response_time)) &&
    Number(finalData.result.response_time) >= 0
  ) {
    nextTrace.durationMs = Math.round(Number(finalData.result.response_time) * 1000);
  }

  return computeTraceSummary(nextTrace);
};

export const getExecutionTraceRenderState = (executionTrace) => {
  const trace = computeTraceSummary(
    cloneTrace(
      executionTrace && typeof executionTrace === "object"
        ? executionTrace
        : createEmptyExecutionTrace(),
    ),
  );

  return {
    ...trace,
    steps: [...trace.steps].sort((a, b) => {
      const orderDelta = Number(a.order || 0) - Number(b.order || 0);
      if (orderDelta !== 0) return orderDelta;
      return (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
    }),
  };
};

const normalizeComparableText = (value) =>
  normalizeString(value)
    .replace(/\bOK\b/gi, "")
    .replace(/\s+/g, " ")
    .replace(/[.]+$/g, "")
    .trim()
    .toLowerCase();

const cleanDisplayText = (value) =>
  normalizeString(value)
    .replace(/^\s*OK\s*[-:·]?\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();

const getArtifactIdentity = (artifact = {}) =>
  normalizeComparableText(
    artifact?.metadata?.filename || artifact?.title || artifact?.metadata?.url,
  );

const toArtifactViewModel = (artifact = {}) => ({
  id:
    artifact?.event_seq != null
      ? `artifact-${artifact.event_seq}`
      : `artifact-${slugify(getArtifactIdentity(artifact) || "file")}`,
  name: cleanDisplayText(artifact?.metadata?.filename || artifact?.title) || "Generated file",
  fileType: normalizeString(artifact?.metadata?.file_extension).toUpperCase(),
  slideCount: Number(artifact?.metadata?.slide_count || 0) || 0,
  fileSize: Number(artifact?.metadata?.file_size || 0) || 0,
  url: normalizeString(
    artifact?.metadata?.url || artifact?.metadata?.download_url || artifact?.url,
  ),
});

const getStepDuration = (step = {}) => {
  const value = Number(step?.metadata?.duration_ms);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

const getCleanStepSummary = (step = {}) => {
  const title = cleanDisplayText(step?.title);
  const summary = cleanDisplayText(step?.summary);
  return summary && normalizeComparableText(summary) !== normalizeComparableText(title)
    ? summary
    : "";
};

/** Builds the user-facing tree consumed by ExecutionTracePanel. */
export const buildExecutionTraceViewModel = (executionTrace) => {
  const renderState = getExecutionTraceRenderState(executionTrace);
  const seenStepKeys = new Set();
  const nodesById = new Map();
  const roots = [];

  renderState.steps.forEach((step, index) => {
    const title = cleanDisplayText(step?.title);
    if (!title) return;
    const id = normalizeString(step?.step_id) || `step-${index}`;
    const duplicateKey = `${normalizeString(step?.parent_step_id)}|${normalizeComparableText(title)}`;
    if (seenStepKeys.has(duplicateKey)) return;
    seenStepKeys.add(duplicateKey);

    const artifacts = [];
    const seenArtifacts = new Set();
    (Array.isArray(step?.metadata?.artifacts) ? step.metadata.artifacts : []).forEach(
      (artifact) => {
        const identity = getArtifactIdentity(artifact);
        if (!identity || seenArtifacts.has(identity)) return;
        seenArtifacts.add(identity);
        artifacts.push(toArtifactViewModel(artifact));
      },
    );

    nodesById.set(id, {
      id,
      title,
      summary: getCleanStepSummary(step),
      duration: getStepDuration(step),
      status: normalizeString(step?.status).toLowerCase() || "running",
      parentStepId: normalizeString(step?.parent_step_id) || null,
      children: [],
      artifacts,
      order: Number(step?.order ?? index),
    });
  });

  nodesById.forEach((node) => {
    const parent = node.parentStepId ? nodesById.get(node.parentStepId) : null;
    if (parent && parent.id !== node.id) parent.children.push(node);
    else roots.push(node);
  });

  const sortNodes = (nodes) => {
    nodes.sort((left, right) => left.order - right.order);
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);

  const attachedArtifactNames = new Set(
    [...nodesById.values()].flatMap((node) => node.artifacts.map((artifact) => artifact.name)),
  );
  const artifacts = [];
  (Array.isArray(renderState.artifacts) ? renderState.artifacts : []).forEach((artifact) => {
    const viewArtifact = toArtifactViewModel(artifact);
    if (attachedArtifactNames.has(viewArtifact.name)) return;
    attachedArtifactNames.add(viewArtifact.name);
    artifacts.push(viewArtifact);
  });

  return {
    title:
      renderState.status === "completed"
        ? "Process completed"
        : renderState.status === "failed"
          ? "Process failed"
          : renderState.status === "blocked"
            ? "Process blocked"
            : renderState.status === "stopped"
              ? "Process stopped"
              : "Working on your request",
    status: renderState.status,
    stepCount: nodesById.size,
    artifactCount:
      artifacts.length +
      [...nodesById.values()].reduce((total, node) => total + node.artifacts.length, 0),
    duration: renderState.durationMs,
    sourcesCount: renderState.sourcesCount,
    steps: roots,
    artifacts,
  };
};

export const isTraceVisible = (executionTrace) =>
  Boolean(
    (Array.isArray(executionTrace?.steps) && executionTrace.steps.length > 0) ||
      (Array.isArray(executionTrace?.artifacts) && executionTrace.artifacts.length > 0),
  );

export const isTerminalTraceStatus = (status) =>
  TERMINAL_STATUSES.has(normalizeString(status).toLowerCase());
